import React, { useMemo, useState } from "react";
import brickMosaicIcon from "./typeofgift-brickmosaic.png";
import giftCardIcon from "./typeofgift-giftcard.png";
import type { GiftType } from "@brickme/project-core/src/model/project.ts";
import { useTranslator } from "@brickme/frontend-common/src/i18n/translation.tsx";
import { formatMoney } from "~/frontend-common/localisation.ts";
import { selectCountries } from "~/features/user/selectors.ts";
import useAppSelector from "~/hooks/use-app-selector.ts";
import GiftingOptionsInfoModal from "./gifting-options-info-modal.tsx";

// Minimum gift card amount
const minimumGiftCardAmount = 50;

type GiftTypeSelectionStepProps = {
	readonly onGiftTypeSelect: (giftType: GiftType) => void;
};

export default function GiftTypeSelectionStep({
	onGiftTypeSelect,
}: GiftTypeSelectionStepProps) {
	const t = useTranslator();
	const [showInfoModal, setShowInfoModal] = useState(false);

	// Get currency code by looking up country based on site code
	const siteCode = useAppSelector((state) => state.reference.siteCode);
	const countries = useAppSelector(selectCountries);

	// Format the minimum gift card amount in the appropriate currency
	const minimumAmount = useMemo(() => {
		// Map site code to country code (uk is special case, others match exactly)
		const siteCountryCode = siteCode.toLowerCase() === "uk" ? "gb" : siteCode;

		// Find country with case-insensitive comparison
		const country = countries.find(
			(c) => c.code.toLowerCase() === siteCountryCode.toLowerCase(),
		);

		if (!country) {
			throw new Error(
				`Country not found for site code: ${siteCode} (looking for country code: ${siteCountryCode})`,
			);
		}

		return formatMoney({
			currencyCode: country.currencyCode,
			amount: minimumGiftCardAmount.toString(),
		});
	}, [siteCode, countries]);

	return (
		<>
			<div className="get-started-step gift-type-selection-step">
				<div className="get-started-content">
					<h1 className="get-started-title">{t("What type of gift?")}</h1>

					<div className="gift-options">
						<button
							className="gift-option"
							onClick={() => onGiftTypeSelect("brick_mosaic")}
							type="button"
						>
							<div className="gift-option-content">
								<div className="gift-icon-section">
									<div className="gift-icon">
										<img src={brickMosaicIcon} alt={t("Brick mosaic")} />
									</div>
								</div>
								<div className="gift-header-section">
									<h2>{t("Brick mosaic")}</h2>
								</div>
								<div className="gift-text-section gift-text-section-long">
									<p className="gift-text-long">
										{t(
											"You select and order the size, and they get a link to create their design. We'll ship the gift directly to them.",
										)}
									</p>
								</div>
							</div>
						</button>

						<button
							className="gift-option"
							onClick={() => onGiftTypeSelect("gift_card")}
							type="button"
						>
							<div className="gift-option-content">
								<div className="gift-icon-section">
									<div className="gift-icon">
										<img src={giftCardIcon} alt={t("Gift card")} />
									</div>
								</div>
								<div className="gift-header-section">
									<h2>{t("Gift card")}</h2>
								</div>
								<div className="gift-text-section">
									<p>
										{t("Choose a digital card value starting at {amount}", {
											amount: minimumAmount,
										})}
									</p>
								</div>
							</div>
						</button>
					</div>

					<div className="gift-options-info-link">
						<button
							type="button"
							onClick={() => setShowInfoModal(true)}
							className="gift-options-info-link-button"
						>
							{t("What's the difference? Learn more about our gifting options")}
						</button>
					</div>
				</div>
			</div>
			<GiftingOptionsInfoModal
				show={showInfoModal}
				onClose={() => setShowInfoModal(false)}
			/>
		</>
	);
}
